/**
 * "Quando serve para você" — a janela de uma espera.
 *
 * Peça compartilhada de propósito: o barbeiro cadastra pelo balcão e o cliente
 * pelo app, mas é o mesmo pedido. Duas cópias divergiriam na primeira vez que
 * alguém acrescentasse um atalho num lado só — e aí a mesma espera passaria a
 * ser descrita de dois jeitos.
 *
 * A escada é intencional: quatro atalhos resolvem quase todo pedido em um
 * toque, e quem precisa de precisão abre "outra faixa". Ninguém encara uma
 * grade de sete dias e dois relógios sem ter pedido por isso.
 */

import {
  DAY_END_MINUTE,
  describeWaitlistWindow,
  WAITLIST_PRESETS,
  WEEKDAY_SHORT,
} from '@barbearia/shared';
import { useState } from 'react';
import { parseHhMm } from '../lib/format.ts';

export interface WaitlistWindowValue {
  weekdays: number[];
  earliestMinute: number;
  latestMinute: number;
}

export function useWaitlistWindow(): {
  value: WaitlistWindowValue;
  picker: React.ReactNode;
} {
  const [preset, setPreset] = useState('qualquer');
  const [custom, setCustom] = useState(false);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('18:00');

  const chosen = WAITLIST_PRESETS.find((entry) => entry.id === preset);

  const value: WaitlistWindowValue = custom
    ? { weekdays, earliestMinute: parseHhMm(from), latestMinute: parseHhMm(to) }
    : {
        weekdays: chosen?.weekdays ?? [],
        earliestMinute: chosen?.earliestMinute ?? 0,
        latestMinute: chosen?.latestMinute ?? DAY_END_MINUTE,
      };

  const picker = (
    <>
      <div className="flex flex-wrap gap-2">
        {WAITLIST_PRESETS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => {
              setPreset(option.id);
              setCustom(false);
            }}
            className={`touch-target rounded-xl border px-4 text-[14px] font-medium ${
              !custom && preset === option.id
                ? 'border-primary bg-primary/10 text-ink'
                : 'border-line bg-surface-raised text-ink-soft'
            }`}
          >
            {option.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCustom(true)}
          className={`touch-target rounded-xl border px-4 text-[14px] font-medium ${
            custom
              ? 'border-primary bg-primary/10 text-ink'
              : 'border-line bg-surface-raised text-ink-soft'
          }`}
        >
          Outra faixa
        </button>
      </div>

      {custom && (
        <div className="mt-3 flex flex-col gap-3 rounded-xl border border-line bg-surface-raised p-3">
          <div className="flex items-center gap-2">
            <label className="flex-1 text-[13px] text-ink-soft">
              A partir das
              <input
                type="time"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                className="mt-1 w-full touch-target rounded-lg border border-line bg-surface px-3 text-[16px] text-ink"
              />
            </label>
            <label className="flex-1 text-[13px] text-ink-soft">
              Até as
              <input
                type="time"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                className="mt-1 w-full touch-target rounded-lg border border-line bg-surface px-3 text-[16px] text-ink"
              />
            </label>
          </div>

          <div>
            <p className="pb-1.5 text-[13px] text-ink-soft">
              Dias — nenhum marcado vale para todos
            </p>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAY_SHORT.map((label, day) => {
                const active = weekdays.includes(day);
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() =>
                      setWeekdays((current) =>
                        active ? current.filter((entry) => entry !== day) : [...current, day],
                      )
                    }
                    className={`h-11 w-11 rounded-lg text-[13px] font-medium ${
                      active
                        ? 'bg-primary text-primary-ink'
                        : 'border border-line bg-surface text-ink-soft'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <p className="px-1 pt-2 text-[13px] text-ink-faint">
        Fica como: <span className="text-ink">{describeWaitlistWindow(value)}</span>
      </p>
    </>
  );

  return { value, picker };
}
